import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import PageHero from '../components/aravi/PageHero';
import Enquiry from '../components/aravi/Enquiry';
import { LANDSCAPES_PAGE } from '../constants/testIds';
import { SETTING_FEATURES, CONNECTIVITY } from '../data/pages';
import { PROJECT } from '../data/content';

export default function LandscapesPage() {
  return (
    <div data-testid={LANDSCAPES_PAGE.root}>
      <PageHero
        eyebrow="The Setting"
        title={<>The neighbours<br/>that won&apos;t change.</>}
        intro="Aravi shares a wall with 1,100 acres of the Osmania Geological Observatory (forest), a state-run hospitality resort directly opposite, and a quiet reservoir ~25 minutes away. The setting is the product."
        image="https://images.unsplash.com/photo-1767677505638-38c958c3e794?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxnbGFzcyUyMGhvdXNlJTIwbmF0dXJlJTIwZm9yZXN0JTIwcmV0cmVhdHxlbnwwfHx8fDE3ODI3MzEyMzZ8MA&ixlib=rb-4.1.0&q=85"
      />

      <section className="bg-[#F5F1E8] py-20 lg:py-28">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto">
          <div className="grid grid-cols-12 gap-6 lg:gap-8">
            {SETTING_FEATURES.map((l, i) => (
              <motion.article
                key={l.slug}
                data-testid={LANDSCAPES_PAGE.card(l.slug)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative overflow-hidden ${
                  i % 5 === 0
                    ? 'col-span-12 md:col-span-7'
                    : i % 5 === 1
                    ? 'col-span-12 md:col-span-5'
                    : i % 5 === 2
                    ? 'col-span-12 md:col-span-5'
                    : i % 5 === 3
                    ? 'col-span-12 md:col-span-7'
                    : 'col-span-12'
                }`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={l.image}
                    alt={l.name}
                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#18342A]/85 via-[#18342A]/10 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-6 lg:p-8 text-[#F5F1E8]">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 text-[10px] tracking-[0.4em] uppercase text-[#C9A458] mb-3">
                          <span>{l.badge}</span>
                        </div>
                        <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight font-light">
                          {l.name}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="mt-5 text-[#6D6A61] text-base font-light max-w-xl">
                  {l.blurb}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Connectivity grid */}
      <section className="bg-[#18342A] text-[#F5F1E8] py-24 lg:py-32 grain-overlay">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto relative z-10">
          <div className="grid grid-cols-12 gap-8 lg:gap-16 items-end mb-16">
            <div className="col-span-12 lg:col-span-7">
              <span className="text-[11px] tracking-[0.4em] uppercase text-[#C9A458]">
                Connectivity
              </span>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-6 leading-tight font-light">
                Fifteen minutes<br/>from the ORR.
              </h2>
            </div>
            <p className="col-span-12 lg:col-span-5 text-[#F5F1E8]/70 text-base leading-relaxed font-light">
              A double-road runs from the ORR exit directly to our gate. Schools,
              IT corridors, the collectorate, and Rajiv Gandhi International
              Airport are all within an easy, signposted drive.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#F5F1E8]/10">
            {CONNECTIVITY.map((c, i) => (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                className="bg-[#18342A] hover:bg-[#1f4233] transition-colors duration-300 p-6 lg:p-8 flex items-center justify-between gap-6"
              >
                <span className="text-[#F5F1E8]/90 font-light text-base md:text-lg">
                  {c.label}
                </span>
                <span className="font-serif text-[#C9A458] text-lg md:text-xl whitespace-nowrap">
                  {c.distance}
                </span>
              </motion.div>
            ))}
          </div>

          <div className="mt-14">
            <a
              href={PROJECT.mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-3 border border-[#C9A458] text-[#C9A458] px-7 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#C9A458] hover:text-[#18342A] transition-colors"
            >
              Open Site Location on Maps
              <ArrowUpRight size={14} strokeWidth={1.6} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      <Enquiry />
    </div>
  );
}
